import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import anchor from '../../../constants/anchor'
import { DATAHUB_API_URL } from '../../api/apiUrls'

import {
    GetAnalyticsPartsAddedApiArg,
    GetAnalyticsPartsAddedApiResponse,
    ChangeBrandForPartsApiArg,
    ChangeBrandForPartsApiResponse,
    ChangeConditionIdForPartsApiArg,
    ChangeConditionIdForPartsApiResponse,
    ChangePriceForPartsApiArg,
    ChangePriceForPartsApiResponse,
    BulkPriceOperationDetailsApiArg,
    BulkPriceOperationDetailsApiResponse,
    RevertBulkPriceChangeApiArg,
    RevertBulkPriceChangeApiResponse,
    ChangeDimensionsAndWeightDataForPartsApiArg,
    ChangeDimensionsAndWeightDataForPartsApiResponse,
    ChangeWarehouseLocationForPartsApiArg,
    ChangeWarehouseLocationForPartsApiResponse,
    GetPartsAdminListApiArg,
    GetPartsAdminListApiResponse,
    GetPartsSelectSearchWarehouseSectionApiArg,
    GetPartsSelectSearchWarehouseSectionApiResponse,
    GetPartsSelectSearchWarehousePlaceApiArg,
    GetPartsSelectSearchWarehousePlaceApiResponse,
    GetUserActiveColumnsCarListApiResponse,
    LinkPartsToCarBulkApiArg,
    LinkPartsToCarBulkApiResponse,
    SaveImageInCloudApiArg,
    SaveImageInCloudApiResponse,
    SetPartsArchivedStatusHandlerApiArg,
    SetPartsArchivedStatusHandlerApiResponse,
    SetUserActiveColumnsCarListApiArg,
    SetUserActiveColumnsCarListApiResponse,
    UpdateSoldPartsFromDealApiArg,
    UpdateSoldPartsFromDealApiResponse,
} from './datahubTypes'

export const BASE_URL = DATAHUB_API_URL

const customBaseQuery = async (args: any, api: any, extraOptions: any) => {
    const baseQuery = fetchBaseQuery({ baseUrl: BASE_URL })
    const userId = localStorage.getItem('userId')
    const accessToken = localStorage.getItem('accessToken')
    const authorizationHeader = `${anchor} ${accessToken}`

    // Добавляем headers с Authorization и userId к каждому запросу
    if (typeof args === 'object') {
        args.headers = {
            ...args.headers,
            Authorization: authorizationHeader,
        }

        if (args.body) {
            args.body = { ...args.body, userId }
        }
    }

    return baseQuery(args, api, extraOptions)
}

export const datahubApi = createApi({
    reducerPath: 'datahubApi',
    baseQuery: customBaseQuery,
    tagTypes: ['UserActiveColumnsCarList'],
    endpoints: builder => ({
        getPartsAdminList: builder.mutation<GetPartsAdminListApiResponse, Omit<GetPartsAdminListApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/getPartsAdminList`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getAnalyticsPartsAdded: builder.mutation<
            GetAnalyticsPartsAddedApiResponse,
            Omit<GetAnalyticsPartsAddedApiArg, 'userId'>
        >({
            query: queryArg => ({
                url: `/getAnalyticsPartsAdded`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getActionLogList: builder.query({
            query: params => ({
                url: '/getActionLogList',
                method: 'POST',
                body: {
                    userId: localStorage.getItem('userId'),
                    params,
                },
            }),
        }),
        updateSoldPartsFromDeal: builder.mutation<UpdateSoldPartsFromDealApiResponse, UpdateSoldPartsFromDealApiArg>({
            query: payload => ({
                url: '/updateSoldPartsFromDeal',
                method: 'POST',
                body: payload,
            }),
        }),
        saveImageInCloud: builder.mutation<SaveImageInCloudApiResponse, SaveImageInCloudApiArg>({
            query: payload => ({
                url: '/saveImageInCloud',
                method: 'POST',
                body: payload,
            }),
        }),
        setPartsArchivedStatusHandler: builder.mutation<
            SetPartsArchivedStatusHandlerApiResponse,
            Omit<SetPartsArchivedStatusHandlerApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/setPartsArchivedStatusHandler',
                method: 'POST',
                body: payload,
            }),
        }),
        linkPartsToCarBulk: builder.mutation<LinkPartsToCarBulkApiResponse, Omit<LinkPartsToCarBulkApiArg, 'userId'>>({
            query: payload => ({
                url: '/linkPartsToCarBulk',
                method: 'POST',
                body: payload,
            }),
        }),
        getPartsSelectSearchWarehouseSection: builder.mutation<
            GetPartsSelectSearchWarehouseSectionApiResponse,
            Omit<GetPartsSelectSearchWarehouseSectionApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/getPartsSelectSearchWarehouseSection',
                method: 'POST',
                body: payload,
            }),
        }),
        getPartsSelectSearchWarehousePlace: builder.mutation<
            GetPartsSelectSearchWarehousePlaceApiResponse,
            Omit<GetPartsSelectSearchWarehousePlaceApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/getPartsSelectSearchWarehousePlace',
                method: 'POST',
                body: payload,
            }),
        }),
        changeDimensionsAndWeightDataForParts: builder.mutation<
            ChangeDimensionsAndWeightDataForPartsApiResponse,
            Omit<ChangeDimensionsAndWeightDataForPartsApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/changeDimensionsAndWeightDataForParts',
                method: 'POST',
                body: payload,
            }),
        }),
        changeWarehouseLocationForParts: builder.mutation<
            ChangeWarehouseLocationForPartsApiResponse,
            Omit<ChangeWarehouseLocationForPartsApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/changeWarehouseLocationForParts',
                method: 'POST',
                body: payload,
            }),
        }),
        getUserActiveColumnsCarList: builder.query<GetUserActiveColumnsCarListApiResponse, void>({
            query: () => ({
                url: '/getUserActiveColumnsCarList',
                method: 'POST',
                body: {},
            }),
            providesTags: ['UserActiveColumnsCarList'],
        }),
        setUserActiveColumnsCarList: builder.mutation<
            SetUserActiveColumnsCarListApiResponse,
            Omit<SetUserActiveColumnsCarListApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/setUserActiveColumnsCarList',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['UserActiveColumnsCarList'],
        }),
        changeBrandForParts: builder.mutation<
            ChangeBrandForPartsApiResponse,
            Omit<ChangeBrandForPartsApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/changeBrandForParts',
                method: 'POST',
                body: payload,
            }),
        }),
        changeConditionIdForParts: builder.mutation<
            ChangeConditionIdForPartsApiResponse,
            Omit<ChangeConditionIdForPartsApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/changeConditionIdForParts',
                method: 'POST',
                body: payload,
            }),
        }),
        changePriceForParts: builder.mutation<
            ChangePriceForPartsApiResponse,
            Omit<ChangePriceForPartsApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/changePriceForParts',
                method: 'POST',
                body: payload,
            }),
        }),
        getBulkPriceOperationDetails: builder.mutation<
            BulkPriceOperationDetailsApiResponse,
            Omit<BulkPriceOperationDetailsApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/getBulkPriceOperationDetails',
                method: 'POST',
                body: payload,
            }),
        }),
        revertBulkPriceChange: builder.mutation<
            RevertBulkPriceChangeApiResponse,
            Omit<RevertBulkPriceChangeApiArg, 'userId'>
        >({
            query: payload => ({
                url: '/revertBulkPriceChange',
                method: 'POST',
                body: payload,
            }),
        }),
    }),
})

export const {
    useGetPartsAdminListMutation,
    useGetAnalyticsPartsAddedMutation,
    useGetActionLogListQuery,
    useUpdateSoldPartsFromDealMutation,
    useSaveImageInCloudMutation,
    useSetPartsArchivedStatusHandlerMutation,
    useLinkPartsToCarBulkMutation,
    useGetPartsSelectSearchWarehouseSectionMutation,
    useGetPartsSelectSearchWarehousePlaceMutation,
    useChangeDimensionsAndWeightDataForPartsMutation,
    useChangeWarehouseLocationForPartsMutation,
    useLazyGetUserActiveColumnsCarListQuery,
    useSetUserActiveColumnsCarListMutation,
    useChangeBrandForPartsMutation,
    useChangeConditionIdForPartsMutation,
    useChangePriceForPartsMutation,
    useGetBulkPriceOperationDetailsMutation,
    useRevertBulkPriceChangeMutation,
} = datahubApi
